import { DeviceContainerStateControllerService } from "../../../services/device-container-state-controller.service";
import { IncidentHistoricalControllerService } from "../../../services/incident-historical-controller.service";
import { DataDecompressorService } from "../../../services/data-decompressor.service";
import { SiteControllerService } from "../../../services/site-controller.service";
import { GeoUtils } from "../../../services/geo-utils";

import { WebSocketService } from "../../../services/web-socket.service";

import { ContainerTypeDTO } from "../../../models/container-type";
import { CurrentStateDTO } from "../../../models/current-state";
import { CurrentStateFilterDTO } from "../../../models/current-state-filter";
import { MapFilter } from "../../../models/map-filter";
import { DataMap } from "../../../models/data-map";
import { SiteDTO } from "../../../models/site";

import { StateDisplayerComponent } from "../../utils/state-displayer/state-displayer.component";
import { StatePopupComponent } from "../../utils/state-popup/state-popup.component";

import {
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  EnvironmentInjector,
  ComponentRef,
  Injector,
  OnInit,
  OnDestroy,
  createComponent,
  ChangeDetectionStrategy
} from "@angular/core";
// NOTE: default import (not `import * as L`) is required under the esbuild
// (@angular/build:application) builder. A namespace import captures a snapshot
// of leaflet's exports, so the `markerClusterGroup`/`heatLayer` methods added by
// the side-effect plugin imports below would be missing. The default import
// binds L to the live leaflet module the plugins mutate.
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.heat";
import { ColorService } from "../../../services/color.service";
import { WsData } from "../../../models";
import { SidenavFiltersComponent } from "../../sidenav/sidenav-filters/sidenav-filters.component";
import { SiteSidenavComponent } from "../../sidenav/site-sidenav/site-sidenav.component";
import { IncidentSidenavComponent } from "../../sidenav/incident-sidenav/incident-sidenav.component";
import { MapDetailComponent } from "./detail/map.detail.component";
import { HeaderMapComponent } from "./header-map/header-map.component";
import { LeafletDirective } from "@bluehalo/ngx-leaflet";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { TranslateModule } from "@ngx-translate/core";
import { MAP_LIST_FILTER_CONFIG } from "../../../services/filter-sync-configs";
import { NavigationNoticeService } from "../../../services/navigation-notice.service";
import { RecordRoute } from "../../../services/record-route";
import { FleetFilter, TrackerFleetFilters } from "../tracker-fleet-filters";

@Component({
  selector: "app-map",
  templateUrl: "./map.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./map.component.scss"],
  imports: [
    SidenavFiltersComponent,
    SiteSidenavComponent,
    IncidentSidenavComponent,
    MapDetailComponent,
    HeaderMapComponent,
    LeafletDirective,
    MatProgressSpinner,
    TranslateModule
  ]
})
export class MapComponent
  extends TrackerFleetFilters
  implements OnInit, OnDestroy
{
  // Internal state for the in-page detail panel (no longer parent-bound inputs
  // after the router migration; the map detail child two-way-binds to these).
  currentState: CurrentStateDTO;
  showDetailMap: boolean;
  // Deep-link target (`/map/tracker/:tracker_id`) while its device is being
  // fetched: set synchronously in ngOnInit so onMapReady skips the all-markers
  // load and goes straight to the detail-focused init path.
  private pendingTrackerId: string | undefined;
  /**
   * The device a click handed over while the navigation to its record URL is in
   * flight. Consumed by `openTrackerFromRoute`, which is the one place the detail
   * opens — see `deviceIdClicked` for why the click cannot open it.
   */
  private clickedState: CurrentStateDTO | undefined;
  /** The `/map/tracker/:tracker_id` segment: what it means to be showing one. */
  private trackerRoute: RecordRoute;
  /** The `/map/site/:site_code` segment, the tracker segment's sibling. */
  private siteRoute: RecordRoute;
  /**
   * Deep-link target (`/map/site/:site_code`) whose SiteDTO cannot be resolved yet:
   * the code names a site in `listSites`, and on a cold load that fetch has not
   * landed. `initSite` is where it is applied — and the only place an unknown code
   * can be called unknown, since until then every code is unresolved.
   */
  private pendingSiteCode: string | undefined;

  public stateDisplayerComponent: ComponentRef<StateDisplayerComponent>;
  public statePopupComponent: ComponentRef<StatePopupComponent>;
  public requestId: number = Math.floor(Math.random() * 200);
  public siteGroup: L.FeatureGroup = new L.FeatureGroup();
  private activeSiteMarker: L.Circle | null = null;
  public allContainerTypes: Array<ContainerTypeDTO>;
  public showSelectionCheckbox: boolean = false;
  public siteCurrentStates: CurrentStateDTO[];
  public reducedClusterDeviceIds: string[];
  public deviceDataOnMap: Array<DataMap>;
  public initialisation: boolean = false;
  public mustRefreshMap: boolean = true;
  public clusterDeviceDatas: DataMap[];
  public markers: L.MarkerClusterGroup;
  public resultRequestId: number = -1;
  public siteMarkers: L.FeatureGroup;
  public site: SiteDTO | undefined;
  public listSites: SiteDTO[];

  public mapFilter: MapFilter;
  public zoomSiteDatas: any;
  public mapReady: boolean;
  public siteLock: boolean;
  public clusterPopup: any;

  public loadingProgress: number = 0;

  public time: number = 0;
  public center: L.Point;
  public radius: number;
  public scale: any;
  public map: any;

  public options = {
    zoom: 6,
    zIndex: 1,
    zoomControl: false,
    center: L.latLng([48, 2]),
    attributionControl: false,
    layers: [
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 18,
        minZoom: 4,
        updateWhenIdle: true
      })
    ]
  };

  public isSocketConnected: boolean = true;

  private lastBounds: L.LatLngBounds | null = null;
  private siteLoadingTimeout1: NodeJS.Timeout;
  private siteLoadingTimeout2: NodeJS.Timeout;
  private finishtimeout: NodeJS.Timeout;
  private timeoutId: NodeJS.Timeout;
  private mapBounds = {
    minLat: null as number | null,
    maxLat: null as number | null,
    minLng: null as number | null,
    maxLng: null as number | null
  };
  private lockMapDevice: boolean = false;
  private isSitesInitialized: boolean = false;
  private isStoreInitialized: boolean = false;

  private readonly NAMESPACE: string = "map";
  /**
   * Zoom a site view opens at: close enough that the individual containers
   * showZoomOnSite draws are legible instead of one cluster blob.
   */
  private readonly SITE_ZOOM: number = 15;
  /**
   * Seconds the camera takes to reach a site. Long enough to read as travel rather
   * than a cut, short enough to be over by the time the site's devices land.
   */
  private readonly SITE_FLY_SECONDS: number = 1.2;

  constructor(
    // dependances
    private environmentInjector: EnvironmentInjector,
    private appRef: ApplicationRef,
    private ref: ChangeDetectorRef,
    private injector: Injector,
    // App services
    private deviceContainerStateControllerService: DeviceContainerStateControllerService,
    public _incidentHistoricalControllerService: IncidentHistoricalControllerService,
    private dataDecompressorService: DataDecompressorService,
    private siteControllerServices: SiteControllerService,
    private wsService: WebSocketService,
    private colorService: ColorService,
    private _geoUtils: GeoUtils,
    private navigationNotice: NavigationNoticeService
  ) {
    super();
    this.listSites = new Array<SiteDTO>();
    // Hydrate from here rather than from the base constructor: a hydrated
    // incident selection resolves its devices straight away, which reaches
    // refreshAfterFilterChange — and that needs this page’s own fields.
    this.hydrateFilters();
    this.initFilters();
    this.trackerRoute = new RecordRoute(
      this.route,
      this.router,
      this.navigationNotice,
      {
        paramKey: "tracker_id",
        basePath: ["/map"],
        // The record is one segment deeper than the list: /map/tracker/:tracker_id.
        recordPath: ["/map", "tracker"],
        open: (deviceId: string) => this.openTrackerFromRoute(deviceId),
        // The URL no longer names a tracker, so tear the panel down — but do NOT
        // navigate. The route has already moved, and /map is not necessarily where
        // it moved to: Back from /map/tracker/:id lands on /map/site/:code just as
        // often, and a navigate here would eject the user from the site view the
        // router had just restored.
        close: () => {
          if (this.showDetailMap) {
            this.teardownDetail();
          }
        },
        // The fleet filters carry across every hop; `pos`/`hrs` belong to the
        // tracker being left behind, so a position from a previously
        // deep-linked tracker cannot follow the next one.
        queryParamsHandling: "merge",
        dropQueryParams: ["pos", "hrs"]
      }
    );
    this.siteRoute = new RecordRoute(
      this.route,
      this.router,
      this.navigationNotice,
      {
        paramKey: "site_code",
        basePath: ["/map"],
        // Sibling of the tracker segment, not a parent of it: /map/site/:site_code.
        recordPath: ["/map", "site"],
        open: (code: string) => this.openSiteFromRoute(code),
        close: () => this.closeSiteFromRoute(),
        queryParamsHandling: "merge",
        dropQueryParams: ["pos", "hrs"]
      }
    );
  }

  /**
   * Seed the colour filter and the saved map view. The filters themselves come
   * from the URL (see the constructor); what is left here is the two pieces of
   * state that are NOT URL-synced: the status list the store is seeded with, and
   * the zoom/centre stored under `mapFilter`.
   */
  private initFilters(): void {
    this.seedColorStatus();

    if (JSON.parse(localStorage.getItem("mapFilter")) !== null) {
      this.mapFilter = JSON.parse(localStorage.getItem("mapFilter"));
      this.options.zoom = this.mapFilter.zoomLevel;
      this.options.center = L.latLng([
        this.mapFilter.centerLat,
        this.mapFilter.centerLng
      ]);
    } else {
      this.mapFilter = new MapFilter();
    }
  }

  private saveMapFilter(): void {
    localStorage.setItem("mapFilter", JSON.stringify(this.mapFilter));
  }

  /**
   * A filter changed: re-fetch and redraw. resetPosition() re-reads the map
   * bounds and reloads from them, so it is the whole reaction for every filter —
   * the colour one included, which the map applies while rendering markers.
   */
  protected refreshAfterFilterChange(_filter: FleetFilter): void {
    this.mustRefreshMap = true;
    this.resetPosition();
  }

  /**
   * A device or incident selection was made by hand, so release the map: the
   * lock exists to keep a deep-linked or clicked tracker in view, and picking
   * from those two filters is the user asking for the fleet back.
   */
  protected override onFilterSet(filter: FleetFilter): void {
    if (filter === "devices" || filter === "incidents") {
      if (this.lockMapDevice) this.lockMapDevice = false;
    }
  }

  /**
   * Initializes the map component, subscribing to websocket and restoring filters.
   * @returns {Promise<void>}
   */
  public async ngOnInit(): Promise<void> {
    // Publish the whole filter set before the websocket await, so the URL is
    // complete right away.
    this.mirrorFilters();
    // Route-driven tracker detail: /map/tracker/:tracker_id (= deviceId) opens
    // that device's detail panel. Followed before the websocket await so the
    // param is read synchronously and onMapReady sees pendingTrackerId. Reacts
    // to deep-links, cross-tab jumps from the list, and back/forward.
    //
    // The tracker goes FIRST and the order is load-bearing. Both segments hang off
    // one paramMap, so a hop between them emits once and both handlers run:
    //  - site -> tracker: the tracker's open synchronously either shows the panel
    //    from the DTO the click handed over or sets pendingTrackerId, so the site's
    //    close sees one of the two and skips its reload.
    //  - tracker -> site: the teardown may schedule a fleet load, and the site's open
    //    then supersedes it — updateMap clears the pending timeout and bumps
    //    requestId, so only the site request survives.
    this.subscription.add(this.trackerRoute.follow());
    this.subscription.add(this.siteRoute.follow());
    await this.subscribeToWebSocket();
  }

  /**
   * Opens the in-page tracker detail for a deep-linked/jumped device id.
   * Fetches the current state by id (replacing the old in-memory DTO handoff)
   * and reuses the popup-click path (deviceIdClicked) to show the detail.
   * @param deviceId {string} The tracker's deviceId from the route param.
   */
  private openTrackerFromRoute(deviceId: string): void {
    if (this.currentState?.deviceId === deviceId && this.showDetailMap) {
      return; // already showing this tracker (e.g. back/forward re-emit)
    }
    // A click on the map or in the device popup navigated here holding the DTO
    // already: open from it, so selecting a device costs no extra request.
    const clicked: CurrentStateDTO | undefined = this.clickedState;
    this.clickedState = undefined;
    if (clicked?.deviceId === deviceId) {
      this.deviceIdClicked(clicked, true);
      this.ref.detectChanges();
      return;
    }
    this.pendingTrackerId = deviceId;
    this.subscription.add(
      this.deviceContainerStateControllerService
        .getClusterCurrentStates([deviceId])
        .subscribe(
          (result: CurrentStateDTO[]) => {
            this.pendingTrackerId = undefined;
            if (result && result.length) {
              this.deviceIdClicked(result[0], true);
              this.ref.detectChanges();
            } else {
              // Unknown tracker id: tell the user the device is gone and fall
              // back to the plain map view.
              this.trackerRoute.notFound();
            }
          },
          () => {
            // Fetch failed (network/backend error): clear the pending state so
            // onMapReady can still load the map instead of staying blank.
            this.pendingTrackerId = undefined;
            this.trackerRoute.toList();
          }
        )
    );
  }

  /**
   * The detail panel's own close affordance: tear it down AND drop the tracker
   * segment, since here the URL still names the tracker being left.
   */
  public onDetailClosed(): void {
    this.teardownDetail();
    this.trackerRoute.toList();
  }

  /**
   * Take the detail panel down and give the fleet back, without touching the URL.
   * Split from `onDetailClosed` because the route-driven close must not navigate:
   * by then the router has already moved, and moving again would overwrite wherever
   * it went (see the tracker route's `close`).
   */
  private teardownDetail(): void {
    this.showDetailMap = false;
    this.currentState = null;
    this.clickedState = undefined;

    if (this.filterSiteCode && this.siteCurrentStates?.length) {
      this.refreshMap(true);
    } else if (!this.filterSiteCode && this.deviceDataOnMap?.length) {
      this.refreshMap();
    } else {
      this.mustRefreshMap = true;
      this.resetPosition();
    }
  }

  /**
   * Called when the Leaflet map is ready. Initializes layers and handlers.
   * @param map {L.Map} The initialized Leaflet map object.
   */
  public onMapReady(map: L.Map): void {
    if (map) {
      this.map = map;
      if (
        !this.currentState &&
        !this.showDetailMap &&
        !this.pendingTrackerId &&
        !this.pendingSiteCode
      ) {
        this.initDatas();
      } else {
        this.initStore();
        if (this.listSites.length === 0 && !this.isSitesInitialized) {
          this.isSitesInitialized = true;
          this.subscription.add(
            this.siteControllerServices.getSites().subscribe((sites) => {
              this.listSites = sites;
              this.initSite();
            })
          );
        }
      }

      const _this = this;
      setTimeout(function () {
        _this.map.invalidateSize();
        _this.mapReady = true;
      });

      if (!this.scale) {
        this.scale = new L.Control.Scale({
          position: "bottomright",
          imperial: false,
          maxWidth: 200,
          updateWhenIdle: true
        }).addTo(this.map);
      }

      this.map.off("moveend", this.onMapMoveEnd);
      this.map.on("moveend", this.onMapMoveEnd);

      // onMapReady fires during the parent tab's change-detection pass, and the
      // synchronous initStore() store subscription populates statusList (bound
      // as [filterStatus]) right after the view was checked with []. Settle the
      // binding in the same tick so the parent's re-check doesn't hit NG0100.
      this.ref.detectChanges();
    }
  }

  private onMapMoveEnd = (_ev: any): void => {
    this.onMapChanged();
  };

  /**
   * Initializes base data: sites and current state, then triggers map update.
   */
  private initDatas(): void {
    this.initialisation = true;
    if (this.listSites.length === 0 && !this.isSitesInitialized) {
      this.isSitesInitialized = true;
      this.subscription.add(
        this.siteControllerServices.getSites().subscribe((res) => {
          this.listSites = res;
          this.initSite();
        })
      );
    }
    this.initStore();
    this.initialisation = false;
    if (this.deviceDataOnMap) {
      this.refreshMap();
    } else {
      this.onMapChanged();
    }
  }

  /**
   * Subscribes to ngrx store to retrieve data used by the map.
   */
  private initStore(): void {
    // Guard against re-subscribing: onMapReady can fire again when the map
    // container is re-created (view toggles), and the store subscriptions live
    // for the component's lifetime, so subscribing more than once would stack
    // duplicate handlers (extra resetPosition/updateMap work on every emission).
    if (this.isStoreInitialized) return;
    this.isStoreInitialized = true;

    this.followColorStatusStore();

    this.subscription.add(
      this.store.ctTypes$.subscribe((ctTypesData: ContainerTypeDTO[]) => {
        this.allContainerTypes = ctTypesData;
        this.mustRefreshMap = true;
        this.resetPosition();
      })
    );
  }

  /**
   * Subscribes to the WebSocket and handles incoming messages.
   * @returns {Promise<void>}
   */
  private async subscribeToWebSocket(): Promise<void> {
    await this.wsService.connect(this.NAMESPACE);

    const socket = this.wsService.getSocket(this.NAMESPACE);
    if (socket) {
      socket.on("connect", () => {
        this.isSocketConnected = true;
      });

      socket.on("disconnect", () => {
        this.isSocketConnected = false;
      });
    }

    this.subscription = this.wsService
      .getNewMessage(this.NAMESPACE)
      .subscribe((msg: any) => {
        if (typeof msg === "string") {
          try {
            msg = JSON.parse(msg);
          } catch {
            console.warn("[MapComponent] Ignoring non-JSON message");
            return;
          }
        }

        if (msg?.requestId !== this.requestId) {
          console.warn(
            `[MapComponent] Ignoring message with requestId ${msg.requestId}, expected ${this.requestId}`
          );
          return;
        }

        if (
          msg?.messageType === "Loading" &&
          typeof msg?.loading === "number"
        ) {
          if (msg.loading <= 50) {
            this.deviceDataOnMap = [];
            this.mapBounds = {
              minLat: null,
              maxLat: null,
              minLng: null,
              maxLng: null
            };
            // Drop the previous response's markers so the next chunk starts
            // from a fresh cluster group (appendDevicesToMap recreates it).
            this.freeMemory();
          }
          this.loadingProgress = msg.loading;

          this.dataDecompressorService
            .unpack(msg.datas)
            .then((unpackedData: Array<DataMap>) => {
              // Append in place to avoid O(n²) reallocation across chunks.
              for (const device of unpackedData) {
                this.deviceDataOnMap.push(device);
              }

              if (
                this?.filterDevices.length ||
                this?.filterIncidentDevices.length
              ) {
                this.setMapBounds(unpackedData);
              }

              // Render only the new chunk's markers instead of rebuilding all.
              this.appendDevicesToMap(unpackedData);
            });
        } else if (msg?.messageType === "Finish") {
          this.loadingProgress = 100;

          if (
            this?.filterDevices.length ||
            this?.filterIncidentDevices.length
          ) {
            if (
              this.mapBounds.minLat !== null &&
              this.mapBounds.maxLat !== null &&
              this.mapBounds.minLng !== null &&
              this.mapBounds.maxLng !== null
            ) {
              const bounds = L.latLngBounds(
                [this.mapBounds.minLat, this.mapBounds.minLng],
                [this.mapBounds.maxLat, this.mapBounds.maxLng]
              );

              this.map.fitBounds(bounds);
              this.lockMapDevice = true;
            }
          }

          this.finishtimeout = setTimeout(() => {
            this.loadingProgress = 0;
          }, 600);
        }
      });
  }

  private setMapBounds(devices: any): void {
    devices.forEach((device) => {
      const { latitude, longitude } = device;

      if (latitude && longitude) {
        if (this.mapBounds.minLat === null || latitude < this.mapBounds.minLat)
          this.mapBounds.minLat = latitude;
        if (this.mapBounds.maxLat === null || latitude > this.mapBounds.maxLat)
          this.mapBounds.maxLat = latitude;
        if (this.mapBounds.minLng === null || longitude < this.mapBounds.minLng)
          this.mapBounds.minLng = longitude;
        if (this.mapBounds.maxLng === null || longitude > this.mapBounds.maxLng)
          this.mapBounds.maxLng = longitude;
      }
    });
  }

  /**
   * Cleans up component state and subscriptions upon destroy.
   * Cleans up all intervals and timeouts.
   */
  public ngOnDestroy(): void {
    if (this.siteLoadingTimeout1) clearTimeout(this.siteLoadingTimeout1);
    if (this.siteLoadingTimeout2) clearTimeout(this.siteLoadingTimeout2);
    if (this.loadingIntervalID) clearInterval(this.loadingIntervalID);
    if (this.finishtimeout) clearTimeout(this.finishtimeout);
    if (this.timeoutId) clearTimeout(this.timeoutId);

    this.subscription.unsubscribe();
    this.wsService.disconnect(this.NAMESPACE);
    this.freeMemory();
    this.map.removeLayer(this.siteGroup);
    this.siteGroup.clearLayers();
    this.siteGroup = undefined;
    this.map = undefined;
  }

  /**
   * Frees up map memory and resources (markers, layers, popups).
   */
  private freeMemory(): void {
    if (this.markers) {
      this.map.removeLayer(this.markers);
      this.markers.clearLayers();
      this.markers = undefined;
    } // important to free memory
    if (this.siteMarkers) {
      this.map.removeLayer(this.siteMarkers);
      this.siteMarkers.clearLayers();
      this.siteMarkers = undefined;
    }
    if (this.statePopupComponent) {
      this.statePopupComponent.destroy();
      this.statePopupComponent = undefined;
    }
    if (this.clusterPopup) {
      this.map.removeLayer(this.clusterPopup);
      this.clusterPopup = undefined;
    }
  }

  /**
   * Manages visibility of site layer based on zoom level.
   * @param map {L.Map} The Leaflet map instance.
   */
  private checkZoomForSite(map: L.Map): void {
    if (map) {
      const zoom = map.getZoom();
      if (zoom > 7) {
        this.map.addLayer(this.siteGroup);
      } else if (zoom < 8) {
        this.map.removeLayer(this.siteGroup);
      }
    }
  }

  /**
   * Triggered when the user finishes moving or zooming the map. Determines whether new data should be fetched.
   */
  public onMapChanged(): void {
    if (
      !this.map ||
      this.initialisation ||
      this.showDetailMap ||
      this.siteLock ||
      this.lockMapDevice
    )
      return;

    const bounds = this.map.getBounds();
    const newCenter = new L.Point(
      bounds.getCenter().lng,
      bounds.getCenter().lat
    );
    const newRadius =
      this.map.distance(bounds.getNorthEast(), bounds.getSouthWest()) / 2000;

    // Always update and save the filter, even if we’re not reloading the data
    if (!this.mapFilter) this.mapFilter = new MapFilter();
    this.mapFilter.centerLng = newCenter.x;
    this.mapFilter.centerLat = newCenter.y;
    this.mapFilter.zoomLevel = this.map.getZoom();
    this.saveMapFilter();
    this.checkZoomForSite(this.map);

    // If the new bounds are already within the old ones, no need to reload
    if (this.lastBounds && this.lastBounds.contains(bounds)) return;

    // If we haven’t moved enough to justify a refresh, don’t reload either
    const distance = this.center
      ? this.map.distance(
          { lat: this.center.y, lng: this.center.x },
          { lat: newCenter.y, lng: newCenter.x }
        ) / 2000
      : Infinity;

    const shouldUpdate =
      !this.center ||
      !this.radius ||
      distance > this.radius / 2.5 ||
      newRadius > 1.5 * this.radius;

    if (!shouldUpdate) return;

    // Update values and refresh the data
    this.center = newCenter;
    this.radius = newRadius;
    this.lastBounds = bounds;
    this.mustRefreshMap = !this.siteLock;

    this.map.invalidateSize();
    this.updateMap();
  }

  /**
   * Initializes site markers and sets up tooltip/click behavior.
   */
  private initSite(): void {
    for (const site of this.listSites) {
      const label: string =
        site.category !== "client"
          ? `<div style='align-items:center'>${site.label}</div> <div style='text-align:center'>${site.code}</div>`
          : site.label;
      const siteMarker = L.circle([site.latitude, site.longitude], {
        color: this.colorService.colors.siteMarker,
        fillColor: this.colorService.colors.siteMarker,
        fillOpacity: 0.4,
        radius: 50,
        bubblingMouseEvents: false
      })
        .bindTooltip(label, {
          direction: "bottom",
          permanent: false,
          className: "leafletCustom",
          opacity: 1
        })
        .addTo(this.siteGroup);

      siteMarker.on("mouseover", (e) => {
        const marker: L.Circle = e.target;

        if (this.activeSiteMarker && this.activeSiteMarker !== marker) {
          this.activeSiteMarker.setRadius(50);
          this.activeSiteMarker.closeTooltip();
        }

        marker.setRadius(site.toleranceRadiusInMeter);
        marker.openTooltip();
        marker.bringToFront();

        this.activeSiteMarker = marker;
      });

      siteMarker.on("mouseout", (e) => {
        const marker: L.Circle = e.target;

        marker.setRadius(50);
        marker.closeTooltip();

        if (this.activeSiteMarker === marker) {
          this.activeSiteMarker = null;
        }
      });

      siteMarker.on("click", (_e) => {
        this.siteRequest(site.code);
      });
    }
    this.checkZoomForSite(this.map);
    // The site the URL names can only be resolved now: until this landed every code
    // looked unknown. Nothing is restored from a previous session either — bare /map
    // is the fleet, and the URL is the only place the open site is remembered.
    //
    // This is the first load, so the camera is set outright rather than flown: it is
    // still on whatever `mapFilter` remembered from the last session, which is not a
    // place the user asked to see and so not a place worth flying from.
    this.applyPendingSite(false);
  }

  /**
   * Enter or leave the site view. Every caller is a user action — a site marker, the
   * filter sidenav, the header — and all any of them does is navigate: the site is
   * `/map/site/:site_code`, and applying it is the route subscription's job, so a
   * click and a deep link take exactly the same path.
   * @param event {string} Site code to open, or undefined to go back to the fleet.
   */
  public siteRequest(event?: string): void {
    if (event) {
      this.siteRoute.goTo(event);
    } else {
      this.siteRoute.toList();
    }
  }

  /**
   * The URL names a site: show it. Resolving the code needs the site list, which a
   * cold deep-link has not fetched yet — so an unresolved code is parked in
   * `pendingSiteCode` and `initSite` finishes the job.
   * @param code {string} The `:site_code` param.
   */
  private openSiteFromRoute(code: string): void {
    this.loadingProgress = 10;
    this.siteLock = true;
    this.pendingSiteCode = code;
    if (this.listSites.length) {
      // The list is already in hand, so the page has been alive and the camera is
      // showing something the user chose: fly from it.
      this.applyPendingSite(true);
    }
  }

  /**
   * Open the parked site code now that `listSites` can resolve it. A code that names
   * no site explains itself and drops back to the fleet — which is only decidable
   * here, because before the list landed every code looked unresolved.
   * @param fly {boolean} True to animate the camera over to the site, false to cut
   * to it. Only the caller knows whether the view being left is one the user chose.
   */
  private applyPendingSite(fly: boolean): void {
    const code: string = this.pendingSiteCode;
    if (!code) return;
    this.pendingSiteCode = undefined;

    const site: SiteDTO = this.listSites.find((s) => s.code === code);
    if (!site) {
      this.siteLock = false;
      this.loadingProgress = 0;
      this.siteRoute.notFound();
      return;
    }
    if (this.site?.code === site.code) return;

    this.site = site;
    this.filterSiteCode = site.code;
    this.mustRefreshMap = true;
    this.updateMap(true);

    // Pin the fetch window to the site so a later camera move is not read as the
    // user asking for a different area.
    const siteLatLng = L.latLng(site.latitude, site.longitude);
    this.center = new L.Point(site.longitude, site.latitude);
    this.radius = site.toleranceRadiusInMeter / 1000; // conversion en km
    this.lastBounds = siteLatLng.toBounds(site.toleranceRadiusInMeter);

    this.moveToSite(site, fly);
  }

  /**
   * Put the camera on a site. Flown, not cut: a site is picked by name out of a
   * list, so an instant jump gives no clue where on the map it landed — flyTo pulls
   * back, travels and zooms in, which reads as somewhere on a map rather than a new
   * screen. It starts as soon as the code resolves, so the move fills the wait for
   * the site's devices instead of following it.
   *
   * The first load is the exception, and it is the caller that knows: there, the
   * camera is wherever the last session left `mapFilter`, which is not a view the
   * user chose and so not one worth watching the map leave.
   * @param site {SiteDTO} The site being opened.
   * @param fly {boolean} False to cut straight to the site.
   */
  private moveToSite(site: SiteDTO, fly: boolean): void {
    const target: L.LatLng = L.latLng(site.latitude, site.longitude);

    if (!fly) {
      this.map.setView(target, this.SITE_ZOOM);
      return;
    }

    this.map.flyTo(target, this.SITE_ZOOM, {
      duration: this.SITE_FLY_SECONDS
    });
  }

  /**
   * The URL names no site: the fleet is what should be showing. Never navigates —
   * the router has already moved, and on the site -> tracker hop it moved to
   * /map/tracker/:id, not to /map.
   */
  private closeSiteFromRoute(): void {
    this.siteLock = false;
    this.mustRefreshMap = true;
    this.site = undefined;
    this.pendingSiteCode = undefined;
    this.filterSiteCode = undefined;
    this.lastBounds = null;
    this.loadingProgress = 0;

    // A flight still in the air would keep moving the camera after the fleet was
    // asked for, and land a moveend that reads as the user panning somewhere new.
    // The site is gone, so is its move.
    this.map?.stop();

    // Before the map exists there is no camera to read, and on the site -> tracker
    // hop the fleet must not be reloaded — the detail replaces it. Both ways of
    // being on that hop count: the tracker's open runs first and has either shown
    // the panel from the DTO the click handed over (showDetailMap) or started
    // fetching it (pendingTrackerId). `mustRefreshMap` above keeps the pending
    // reload for onMapReady and for the detail's teardown.
    if (this.map && !this.showDetailMap && !this.pendingTrackerId) {
      this.resetPosition();
    }
  }

  /**
   * Recalculates the map center and radius, and updates the map.
   */
  private resetPosition(): void {
    const bounds = this.map.getBounds();
    this.center = new L.Point(
      this.map.getCenter().lng,
      this.map.getCenter().lat
    );
    this.radius =
      this.map.distance(bounds.getNorthEast(), bounds.getSouthWest()) / 2000;
    this.updateMap();
  }

  /**
   * Triggers map data loading based on site mode or general mode.
   * @param newSite {boolean} Optional flag to force reload when entering a site.
   */
  private updateMap(newSite?: boolean): void {
    // A parked deep-link site issues its own request the moment `listSites` lands,
    // and until then `siteLock` is already on while `this.site` is still unset — so
    // anything scheduled here would reach sendSiteRequest with no site to ask about.
    // `mustRefreshMap` keeps the pending reload either way.
    if (this.pendingSiteCode) return;

    this.requestId++;
    const id = this.requestId;

    if (this.timeoutId) clearTimeout(this.timeoutId);

    // start the request after a short delay to handle rapid changes
    this.timeoutId = setTimeout(() => {
      if (this.requestId === id && (this.mustRefreshMap || newSite)) {
        if (this.siteLock) {
          this.sendSiteRequest();
        } else {
          this.sendMapRequest();
        }
      }
    }, 1000);
  }

  /**
   * Simaulates loading progress for site data.
   */
  private simulateSiteLoadingProgress(): void {
    this.loadingProgress = 10;

    const interval = setInterval(() => {
      this.loadingProgress += 10;
      if (this.loadingProgress >= 90) {
        clearInterval(interval);
      }
    }, 150);
  }
  /**
   *
   * Sends a request for site-specific data.
   */
  private sendSiteRequest(): void {
    this.simulateSiteLoadingProgress();

    this.subscription.add(
      this.deviceContainerStateControllerService
        .getCurrentStatesBySite({
          requestId: this.requestId,
          filterContainers: this.filterContainers,
          filterContainerCategory: this.filterContainerCategory,
          containerTypes: this.allContainerTypes,
          siteId: this.site.code,
          filtersColor: this.statusList,
          filterDevicesId: !this.filterIncidentDevices?.length
            ? this.filterDevices
            : this.filterIncidentDevices,
          filterLastSites: this.filterLastSites,
          filterConfidenceRadius: this.filterConfidenceRadius,
          filterDevicesTypes: this.filterDevicesTypes,
          filterStatusDays: this.filterStatusDays,
          filterTorn: this.filterTorn,
          filterUnknown: this.filterUnknown
        })
        .subscribe((result) => {
          this.dataDecompressorService
            .unpack(result.partialCurrentStates)
            .then((partialCurrentStates) => {
              if (result.requestId === this.requestId) {
                let siteResponse = {};
                for (const property in result) {
                  siteResponse[property] =
                    property === "partialCurrentStates"
                      ? partialCurrentStates
                      : result[property];
                }
                this.mustRefreshMap = false;
                this.siteCurrentStates = partialCurrentStates;
                // The camera is deliberately left alone: opening the site already
                // flew it there, and a refresh from inside a site view — a filter
                // change — must not haul the user back off whatever they zoomed
                // into. See moveToSite.
                this.refreshMap(true);
                this.zoomSiteDatas = siteResponse;

                setTimeout(() => {
                  this.loadingProgress = 100;
                  setTimeout(() => {
                    this.loadingProgress = 0;
                  }, 500);
                }, 200);
              }
            });
        })
    );
  }

  /**
   * Refreshes the map view based on data, switching between cluster and detail view.
   * @param noCluster {boolean} Optional flag to skip clustering.
   */
  public refreshMap(noCluster?: boolean): void {
    if (!this.showDetailMap) {
      if (!Array.isArray(this.deviceDataOnMap) && !noCluster) {
        console.error("Error, no map data to display");
        return;
      }
      if (noCluster === true) {
        this.showZoomOnSite(this.siteCurrentStates);
      } else {
        this.clusterizeContainers(this.deviceDataOnMap);
      }
    }
  }

  /**
   * Sends a general map data request through the websocket.
   */
  private loadingIntervalID: any;
  public sendMapRequest(): void {
    let cType = Array<string>();

    if (this.filterContainers?.length) {
      cType = this.filterContainers;
    } else {
      this.allContainerTypes.forEach((containerType) => {
        if (containerType.checked) {
          cType.push(containerType.code);
        }
      });
    }

    cType = this.deviceContainerStateControllerService.applyCategoryFilter(
      cType,
      {
        containerTypes: this.allContainerTypes,
        filterContainerCategory: this.filterContainerCategory
      } as CurrentStateFilterDTO
    );

    // Guard on the RESOLVED cType, after the category filter — same convention
    // as the HTTP callers in device-container-state-controller.service.ts
    // (getStateColorsIndicator & co. return null on cType.length === 0), so an
    // empty list is never sent and never interpreted by the backend as "all".
    // Covers both cases: the authorized container types have not been seeded
    // yet (the dashboard seeds them asynchronously, and the store subscription
    // re-triggers this request once they arrive), and a category filter that
    // excluded every selected type.
    if (!cType.length) {
      return;
    }

    const requestBody = {
      requestId: this.requestId,
      centerLat: this.center.y,
      centerLng: this.center.x,
      radius: this.radius,
      containerTypes: cType,
      devicesId: !this.filterIncidentDevices?.length
        ? this.filterDevices
        : this.filterIncidentDevices
    };

    const requestFilter = JSON.parse(
      this.deviceContainerStateControllerService.createListFilter({
        requestId: this.requestId,
        filterContainers: this.filterContainers,
        containerTypes: this.allContainerTypes,
        centerLat: this.center.y,
        centerLng: this.center.x,
        radius: this.radius,
        filtersColor: this.statusList,
        filterDevicesId: !this.filterIncidentDevices?.length
          ? this.filterDevices
          : this.filterIncidentDevices,
        filterLastSites: this.filterLastSites,
        filterStatusDays: this.filterStatusDays,
        filterConfidenceRadius: this.filterConfidenceRadius,
        filterDevicesTypes: this.filterDevicesTypes,
        filterTorn: this.filterTorn,
        filterUnknown: this.filterUnknown
      })
    );

    const wsReferenceData: WsData = {
      messageType: "Init",
      referenceType: "Map",
      datas: [requestBody, requestFilter],
      userName: ""
    };

    this.wsService.sendMessage(wsReferenceData, this.NAMESPACE);

    if (this.loadingIntervalID) {
      clearInterval(this.loadingIntervalID);
    }

    this.loadingProgress = 0;
    this.loadingIntervalID = setInterval(() => {
      if (this.loadingProgress <= 40) {
        this.loadingProgress += 10;
      } else {
        clearInterval(this.loadingIntervalID);
      }
    }, 300);
  }

  /**
   * Displays markers and heatmap for a site-focused data set.
   * @param currentStateList {Array<CurrentStateDTO>} The list of current states.
   */
  private showZoomOnSite(currentStateList: Array<CurrentStateDTO>): void {
    this.displayClusterizedMarkers<CurrentStateDTO>(currentStateList, {
      isSite: true,
      getLatLng: (d) => [d.latitudeComputed, d.longitudeComputed],
      getColor: (d) => d.statusColor,
      getDeviceId: (d) => d.deviceId,
      getItem: (m) => m.item
    });
  }

  /**
   * Displays clustered markers for general device data.
   * @param deviceDatas {Array<DataMap>} The dataset to clusterize.
   */
  private clusterizeContainers(deviceDatas: Array<DataMap>): void {
    this.displayClusterizedMarkers<DataMap>(deviceDatas, {
      isSite: false,
      getLatLng: (d) => [d.latitude, d.longitude],
      getColor: (d) => d.statusColor,
      getDeviceId: (d) => d.deviceId,
      getItem: (m) => m.item
    });
  }

  /**
   * Incrementally adds a streamed chunk of devices to the map without
   * rebuilding the markers already displayed. Creates the cluster group on the
   * first chunk of a response, then bulk-adds only the new chunk's markers.
   * @param chunk {Array<DataMap>} The newly received devices to render.
   */
  private appendDevicesToMap(chunk: Array<DataMap>): void {
    if (this.showDetailMap) return;

    // First chunk of the response: (re)create a fresh cluster group once.
    if (!this.markers) {
      this.createClusters();
      this.map.addLayer(this.markers);
    }

    const newMarkers: any[] = [];
    for (const device of chunk) {
      const marker = this.buildMarker<DataMap>(device, {
        getLatLng: (d) => [d.latitude, d.longitude],
        getColor: (d) => d.statusColor,
        getDeviceId: (d) => d.deviceId
      });
      if (marker) newMarkers.push(marker);
    }

    if (newMarkers.length) {
      // Bulk insertion is far cheaper than addLayer-per-marker + full rebuild.
      this.markers.addLayers(newMarkers);
    }
  }

  /**
   * Builds a single Leaflet marker (icon + popup + click handler) for an item.
   * @template T - Generic data type (CurrentStateDTO or DataMap).
   * @param item {T} - The data item to render.
   * @param options {Object} - Configuration for rendering.
   * @returns {any} The built marker, or null when lat/lng are missing.
   */
  private buildMarker<T>(
    item: T,
    options: {
      getLatLng: (item: T) => [number, number];
      getColor: (item: T) => string;
      getDeviceId: (item: T) => string;
      onPopupClick?: (item: T) => void;
    }
  ): any {
    const [lat, lng] = options.getLatLng(item);
    if (lat === undefined || lng === undefined) return null;

    const customMarker: any = L.Marker.extend({ color: "" });
    const customIcon: any = L.Icon.extend({
      options: {
        iconAnchor: [20, 50],
        iconSize: [40, 50],
        popupAnchor: [0, -50]
      }
    });

    const icon = this._geoUtils.getMarkerIcon(
      options.getColor(item),
      options.getColor(item) === "GREY",
      false
    );

    const popupContainer = L.DomUtil.create("div", "popup");
    const popup = L.popup({
      offset: [0, 15],
      closeButton: false,
      maxWidth: 768
    })
      .setLatLng([lat, lng])
      .setContent(popupContainer)
      .on("remove", () => {
        popupContainer.innerHTML = "";
        if (this.stateDisplayerComponent) {
          this.stateDisplayerComponent.destroy();
          this.stateDisplayerComponent = undefined;
        }
      });

    const marker = new customMarker([lat, lng], {
      icon: new customIcon({ iconUrl: icon }),
      color: options.getColor(item)
    })
      .bindPopup(popup)
      .closePopup();

    marker.item = item;

    marker.on("click", () => {
      if (popupContainer.childNodes.length === 0) {
        const content = this.createPopup(options.getDeviceId(item));
        popupContainer.appendChild(content);
        if (options.onPopupClick) options.onPopupClick(item);
      }
    });

    return marker;
  }

  /**
   * Displays markers and clusters for both site and general map views.
   * @template T - Generic data type (CurrentStateDTO or DataMap).
   * @param data {T[]} - List of data items to be rendered as markers.
   * @param options {Object} - Configuration for rendering.
   */
  private displayClusterizedMarkers<T>(
    data: T[],
    options: {
      isSite: boolean;
      getLatLng: (item: T) => [number, number];
      getColor: (item: T) => string;
      getDeviceId: (item: T) => string;
      getItem: (marker: any) => T;
      onPopupClick?: (item: T) => void;
    }
  ): void {
    this.freeMemory();
    this.createClusters();

    const latLngs: L.LatLng[] = [];
    for (const item of data) {
      const marker = this.buildMarker(item, options);
      if (!marker) continue;

      const [lat, lng] = options.getLatLng(item);
      this.markers.addLayer(marker);
      latLngs.push(new L.LatLng(lat, lng));
    }

    if (options.isSite && latLngs.length) {
      const heatmap = L.heatLayer(latLngs, { radius: 30, max: 5 });
      this.siteMarkers = L.featureGroup([this.markers, heatmap]);
      this.map.addLayer(this.siteMarkers);
    } else {
      this.map.addLayer(this.markers);
    }
  }

  /**
   * Finds the most frequent color from a list, based on priority.
   * @param array {string[]} The array of color codes.
   * @returns {string | null} The dominant color or null.
   */
  private getMostFrequentColor(array: string[]): string | null {
    if (!array.length) return null;

    const frequencyMap = {};
    let maxCount: number = 0;
    let mostFrequentColor: string | null = null;
    const colorPriority = {
      RED: 4,
      ORANGE: 3,
      BLUE: 2,
      GREEN: 1,
      GREY: 0
    };

    for (const color of array) {
      if (frequencyMap[color]) {
        frequencyMap[color]++;
      } else {
        frequencyMap[color] = 1;
      }
      if (
        frequencyMap[color] === maxCount &&
        colorPriority[color] > colorPriority[mostFrequentColor] &&
        color !== "GREY"
      ) {
        mostFrequentColor = color;
      } else if (frequencyMap[color] > maxCount && color !== "GREY") {
        maxCount = frequencyMap[color];
        mostFrequentColor = color;
      }
    }

    if ("GREY" in frequencyMap && frequencyMap["GREY"] === array.length) {
      return "GREY";
    } else {
      return mostFrequentColor;
    }
  }

  /**
   * Creates a marker cluster group with custom icons.
   */
  private createClusters(): void {
    const _this = this;
    this.markers = L.markerClusterGroup({
      iconCreateFunction: function (cluster) {
        let customIconCluster: any = L.DivIcon.extend({});
        const childColors = [];
        let iconURL = "/assets/img/clusters/cluster.png";

        for (const marker of cluster.getAllChildMarkers() as any) {
          childColors.push(marker.options.color);
        }
        const mostFrequentColor = _this.getMostFrequentColor(childColors);
        if (
          ["RED", "ORANGE", "BLUE", "GREEN", "GREY"].includes(mostFrequentColor)
        ) {
          iconURL = `/assets/img/clusters/cluster-${mostFrequentColor.toLowerCase()}.png`;
        }
        const divCluster = L.DomUtil.create("div", "divCluster");

        const divChildCount = L.DomUtil.create(
          "span",
          "childCount",
          divCluster
        );
        divChildCount.innerHTML = _this.formatCount(
          cluster.getAllChildMarkers().length
        );
        divChildCount.style.position = "absolute";
        divChildCount.style.zIndex = "100";
        divChildCount.style.top = "40%";
        divChildCount.style.left = "50%";
        divChildCount.style.fontSize = "medium";
        divChildCount.style.transform = "translate(-50%, -50%)";

        const divIconCluster = L.DomUtil.create(
          "img",
          "iconCluster",
          divCluster
        ) as HTMLImageElement;
        divIconCluster.src = iconURL;
        divIconCluster.style.width = "40px";
        divIconCluster.style.height = "50px";

        return new customIconCluster({
          html: divCluster.innerHTML,
          className: "div-cluster"
        });
      },

      zoomToBoundsOnClick: false,
      spiderfyOnMaxZoom: false,
      showCoverageOnHover: false,
      // Time-slice large insertions so bulk addLayers never freezes the UI.
      chunkedLoading: true,
      // Skip markers outside the viewport to reduce DOM/CPU on dense maps.
      removeOutsideVisibleBounds: true
    });

    // CLUSTER POPUP HERE
    this.markers.on("clusterclick", (e: any) => {
      const layers = e.layer.getAllChildMarkers();
      this.clusterDeviceDatas = [...layers]
        .map((layer) => layer.item)
        .sort((a: DataMap, b: DataMap) =>
          a.receivedMessageTime > b.receivedMessageTime
            ? -1
            : a.receivedMessageTime > b.receivedMessageTime
              ? 0
              : 1
        );
      let clusterDeviceIds: string[] = this.clusterDeviceDatas.map(
        (device) => device.deviceId
      );
      let first10DeviceIds: string[] = clusterDeviceIds.slice(0, 30);
      const popup = L.DomUtil.create("div", "popup-cluster");
      this.statePopupComponent = createComponent(StatePopupComponent, {
        environmentInjector: this.environmentInjector, // à injecter
        elementInjector: this.injector // ton injector existant
      });
      this.appRef.attachView(this.statePopupComponent.hostView);
      this.statePopupComponent.onDestroy(() => {
        if (this.showCreateIncident) this.showCreateIncident = false;
        this.listIncidentDevices = [];
        this.isEmptyIncidentDevice = true;
        this.appRef.detachView(this.statePopupComponent.hostView);
      });
      popup.appendChild(this.statePopupComponent.location.nativeElement);
      var _this = this;
      this.clusterPopup = L.popup({
        closeButton: false,
        maxWidth: 1300,
        minWidth: 1300,
        offset: [0, -40]
      })
        .setLatLng(e.layer.getLatLng())
        .setContent(popup)
        .openOn(this.map)
        .on("remove", function () {
          _this.showSelectionCheckbox = false;
          _this.ref.detectChanges();
          if (_this.statePopupComponent) _this.statePopupComponent.destroy();
        });

      this.subscription.add(
        this.deviceContainerStateControllerService
          .getClusterCurrentStates(first10DeviceIds)
          .subscribe((result) => {
            this.statePopupComponent.instance.currentStates = result;
            this.statePopupComponent.instance.clusterDeviceIds =
              clusterDeviceIds;
            this.statePopupComponent.instance.clicked.subscribe(
              (currentState) => this.deviceIdClicked(currentState)
            );
            this.statePopupComponent.instance.clickedIncident.subscribe(
              (info) => {
                this.onChangeTab(info);
              }
            );
            this.statePopupComponent.instance.handleDevice.subscribe((device) =>
              this.handleDeviceIncident(device)
            );
            this.statePopupComponent.changeDetectorRef.detectChanges();

            let reducedClusterDeviceDatas = this.clusterDeviceDatas.filter(
              (element) =>
                element.statusColor === "RED" ||
                element.statusColor === "ORANGE"
            );
            if (
              reducedClusterDeviceDatas.length >= 1 &&
              reducedClusterDeviceDatas.length <= 150
            ) {
              this.showSelectionCheckbox = true;
              this.reducedClusterDeviceIds = reducedClusterDeviceDatas.map(
                (device) => device.deviceId
              );
            }
          })
      );
    });
  }

  /**
   * Creates a popup for a specific device ID.
   * @param deviceId {string} The device ID to display.
   * @returns {HTMLDivElement} The popup DOM element.
   */
  private createPopup(deviceId: string): HTMLDivElement {
    const popupElement = L.DomUtil.create("div", "popup-cluster");

    this.statePopupComponent = createComponent(StatePopupComponent, {
      environmentInjector: this.environmentInjector,
      elementInjector: this.injector
    });

    this.appRef.attachView(this.statePopupComponent.hostView);
    this.statePopupComponent.onDestroy(() => {
      this.appRef.detachView(this.statePopupComponent.hostView);
    });
    popupElement.appendChild(this.statePopupComponent.location.nativeElement);

    this.subscription.add(
      this.deviceContainerStateControllerService
        .getClusterCurrentStates([deviceId])
        .subscribe((result) => {
          this.statePopupComponent.instance.currentStates = result;
          this.statePopupComponent.instance.clusterDeviceIds = [deviceId];
          this.statePopupComponent.instance.clicked.subscribe((currentState) =>
            this.deviceIdClicked(currentState)
          );
          this.statePopupComponent.instance.clickedIncident.subscribe(
            (info) => {
              this.onChangeTab(info);
            }
          );
          this.statePopupComponent.instance.handleDevice.subscribe((device) =>
            this.handleDeviceIncident(device)
          );
          this.statePopupComponent.changeDetectorRef.detectChanges();
        })
    );

    return popupElement;
  }

  /**
   * Called when a device ID is clicked in popup, switches to detail view.
   *
   * A click only records the device and navigates: the URL is what opens the
   * panel, through `openTrackerFromRoute`. Showing it here instead would build the
   * detail child before the router had committed `/map/tracker/:id` — and that
   * child mirrors `hrs`/`pos` to the URL as it initialises, relative to an
   * ActivatedRoute still sitting on `/map`. That second navigation SUPERSEDES the
   * record one, so the panel opened and the URL never left `/map`.
   *
   * @param currentState {CurrentStateDTO} The clicked device state.
   * @param fromRoute {boolean} True when the route drove this open.
   */
  private deviceIdClicked(
    currentState: CurrentStateDTO,
    fromRoute: boolean = false
  ): void {
    if (!fromRoute) {
      // Handed over rather than re-fetched: the click already carries the DTO the
      // route callback would otherwise ask the backend for.
      this.clickedState = currentState;
      this.trackerRoute.goTo(currentState.deviceId);
      return;
    }

    // The panel sets its own view, so hand the camera over: a site flight still
    // running would drag it back off the tracker mid-animation.
    this.map?.stop();

    this.freeMemory();
    this.showSelectionCheckbox = false;
    this.showDetailMap = true;
    this.currentState = currentState;
  }

  /**
   * Cross-tab jump from map detail to the list view. Clean-URL navigation;
   * shared filters carry across in the query string. Replaces the old in-memory
   * DTO handoff (the list has no per-device detail route).
   * @param _event {any} Event info (unused; kept for the child binding).
   */
  public toList(_event: any): void {
    // Cross-page hop: map and list share MAP_LIST_FILTER_CONFIG, so carry those
    // params over (and only those). The tab bar's plain routerLink to /list
    // deliberately does not — a tab click is a fresh page view.
    this.filterSync.navigateCarryingFilters(
      this.route,
      ["/list"],
      MAP_LIST_FILTER_CONFIG
    );
  }

  /**
   * Formats large numbers into abbreviated display (e.g., 1.2k).
   * @param count {number} The count value.
   * @returns {string} The formatted count string.
   */
  private formatCount(count: number): string {
    if (localStorage.getItem("profilId") === "IBMAdmin") {
      return count.toString();
    }
    if (count > 9999) {
      return (count / 1000).toFixed(0) + "k";
    } else if (count > 999) {
      return (count / 1000).toFixed(1) + "k";
    } else {
      return count.toString();
    }
  }
}
